import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
export function useBackups(filter) {
    // Backend's list_backups accepts limit/offset; the FE filter speaks page/limit
    // for ergonomics. Translate here so Next/Prev actually moves the window.
    const limit = filter.limit ?? 20;
    const page = filter.page ?? 1;
    const params = new URLSearchParams();
    params.set('limit', String(limit));
    params.set('offset', String((page - 1) * limit));
    Object.entries(filter).forEach(([k, v]) => {
        if (k === 'page' || k === 'limit')
            return;
        if (Array.isArray(v))
            v.forEach((x) => params.append(k, String(x)));
        else if (v !== undefined && v !== null && v !== '')
            params.set(k, String(v));
    });
    return useQuery({
        queryKey: ['backups', filter],
        queryFn: () => api(`/backups?${params}`),
        // 10s baseline. Mutations (run-now / soft-delete / purge) call
        // `qc.invalidateQueries(['backups'])` directly so user-initiated
        // changes show up immediately — polling here covers passive
        // transitions (cron-scheduled backup starts, worker finishes a run).
        // Pause when tab hidden — no point polling a backgrounded panel.
        refetchInterval: 10_000,
        refetchIntervalInBackground: false,
    });
}
export function useBackup(id) {
    return useQuery({
        queryKey: ['backups', 'detail', id],
        queryFn: () => api(`/backups/${id}`),
        enabled: !!id,
    });
}
export function useSoftDeleteBackup() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id) => api(`/backups/${id}`, { method: 'DELETE' }),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['backups'] }),
    });
}
export function usePurgeBackup() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id) => api(`/backups/${id}/purge_now`, {
            method: 'POST',
            body: { confirm_phrase: 'PURGE' },
        }),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['backups'] }),
    });
}
export function useDownloadBackup() {
    return useMutation({
        mutationFn: (id) => api(`/backups/${id}/download-token`, { method: 'POST' }),
    });
}
export function useRestoreBackup() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input) => api(`/backups/${input.backup_id}/restore`, {
            method: 'POST',
            body: {
                target_host: input.target_host,
                target_port: input.target_port,
                target_db_name: input.target_db_name,
                target_user: input.target_user,
                target_password: input.target_password,
            },
        }),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['jobs'] }),
    });
}
